import React, { useState, useCallback } from 'react';
import {
  FiFileText, FiBook, FiDownload, FiExternalLink, FiBarChart2, FiUsers,
  FiSettings, FiMap, FiSmartphone, FiLayers, FiTrendingUp, FiGlobe,
  FiCode, FiDatabase, FiPackage, FiServer, FiCpu, FiShield, FiDollarSign,
  FiTarget, FiAward, FiChevronDown, FiChevronUp, FiLock, FiAlertTriangle
} from 'react-icons/fi';
import deploymentConfig from '../config/deployment';
import { useAuth } from '../contexts/AuthContext';
import { getDocumentationSections } from '../config/permissions';
import { useTranslation } from '../hooks/useTranslation';

const Documentation = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const userRole = user?.role || 'editor';
  const docsUrl = deploymentConfig.publicUrl || deploymentConfig.apiBaseUrl;
  const apiUrl = deploymentConfig.apiBaseUrl;
  // Remove /api suffix from apiBaseUrl to get base domain for docs (nginx serves /docs/ directly)
  const baseUrl = (deploymentConfig.apiBaseUrl || '').replace(/\/api$/, '');

  const [expandedSections, setExpandedSections] = useState({
    cms: true,
    deployment: false,
    app: false,
    blockchain: false,
    marketing: false,
    technical: true,
    masterOnly: false
  });

  // Document viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerContent, setViewerContent] = useState('');
  const [viewerTitle, setViewerTitle] = useState('');
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState(null);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  /**
   * Convert a document URL to a protected API endpoint URL
   */
  const getProtectedUrl = useCallback((url) => {
    if (!url) return null;

    // Get base URL - remove /api suffix if present, handle both cases
    const baseUrl = (apiUrl || '').endsWith('/api')
      ? apiUrl.slice(0, -4)  // Remove '/api' from the end
      : apiUrl;

    // Handle /documentation/tech/* URLs - master_admin only
    if (url.includes('/documentation/tech/')) {
      const filePath = url.split('/documentation/tech/')[1];
      return `${baseUrl}/api/protected-docs/tech/${filePath}`;
    }

    // Handle /documentation/* URLs (general docs)
    if (url.includes('/documentation/')) {
      const filePath = url.split('/documentation/')[1];
      return `${baseUrl}/api/protected-docs/general/${filePath}`;
    }

    // Handle /docs/* URLs
    if (url.match(/\/docs\/(?!view)/)) {
      const filePath = url.split('/docs/')[1];
      return `${baseUrl}/api/protected-docs/files/${filePath}`;
    }

    // URLs that go through /api/docs/* are already authenticated
    return url;
  }, [apiUrl]);

  /**
   * Open a document in the viewer with authentication
   */
  const openDocument = useCallback(async (url, title) => {
    setViewerTitle(title);
    setViewerLoading(true);
    setViewerError(null);
    setViewerOpen(true);
    setViewerContent('');

    try {
      const protectedUrl = getProtectedUrl(url);

      // For /api/docs/* endpoints, use them directly
      // For other URLs, fetch with auth headers
      const fetchUrl = protectedUrl || url;

      const response = await fetch(fetchUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/html, text/markdown, */*'
        }
      });

      if (response.status === 401) {
        setViewerError('Authentication required. Please log in again.');
        return;
      }

      if (response.status === 403) {
        setViewerError('Access denied. You do not have permission to view this document. Tech documentation requires master_admin role.');
        return;
      }

      if (!response.ok) {
        setViewerError(`Failed to load document: ${response.statusText}`);
        return;
      }

      const content = await response.text();
      setViewerContent(content);
    } catch (error) {
      console.error('Error loading document:', error);
      setViewerError('Failed to load document. Please try again.');
    } finally {
      setViewerLoading(false);
    }
  }, [token, getProtectedUrl]);

  /**
   * Close the document viewer
   */
  const closeViewer = useCallback(() => {
    setViewerOpen(false);
    setViewerContent('');
    setViewerTitle('');
    setViewerError(null);
  }, []);

  const documentSections = {
    cms: {
      title: 'CMS & Admin Dashboard',
      icon: FiSettings,
      color: 'from-blue-500 to-cyan-500',
      description: 'Complete content management system documentation',
      roles: ['master_admin', 'admin', 'editor'],
      documents: [
        {
          title: 'CMS User Guide',
          description: 'Comprehensive guide for managing challenges, users, NFTs, and content through the admin dashboard',
          icon: FiBook,
          type: 'guide',
          url: `${baseUrl}/docs/CMS_USER_GUIDE.md`,
          topics: [
            'Dashboard Overview & Analytics',
            'User Management & Roles',
            'Challenge Management with Interactive Maps',
            'Location Search & GPS Configuration',
            'AI Challenge Generation (Claude-powered)',
            'NFT Template Designer',
            'Gallery Management System',
            'Landing Page Editor',
            'System Settings & Blockchain Configuration',
            'Terminal Access (2FA-secured)'
          ]
        },
        {
          title: 'Settings Configuration',
          description: 'How to configure system settings, blockchain networks, and branding',
          icon: FiSettings,
          type: 'technical',
          url: `${baseUrl}/docs/SETTINGS_CONFIGURATION.md`,
          topics: [
            'General Settings (App Name, Tagline)',
            'User Management & Admin Roles',
            'Email Configuration',
            'Security Settings',
            'Blockchain Network Selection',
            'Multi-Chain Configuration (Algorand, Solana, Polygon)',
            'White-Label Branding'
          ]
        },
        {
          title: 'Statistics & Analytics',
          description: 'Understanding the analytics dashboard and generating insights',
          icon: FiBarChart2,
          type: 'guide',
          url: `${baseUrl}/docs/STATISTICS_ANALYTICS.md`,
          topics: [
            'User Activity Metrics',
            'Challenge Completion Rates',
            'Geographic Heat Maps',
            'Popular Challenges & Locations',
            'NFT Minting Statistics',
            'Real-Time System Health'
          ]
        }
      ]
    },
    deployment: {
      title: 'Public Website Deployment',
      icon: FiGlobe,
      color: 'from-green-500 to-emerald-500',
      description: 'Landing pages and public-facing content',
      roles: ['master_admin', 'admin'],
      documents: [
        {
          title: 'Landing Page System',
          description: 'How to customize and deploy the public-facing landing page',
          icon: FiCode,
          type: 'technical',
          url: `${baseUrl}/docs/LANDING_PAGE_SYSTEM.md`,
          topics: [
            'Landing Page Editor (CMS)',
            'Hero Section with Custom Backgrounds',
            'Discover Section (Asymmetric Grid Layout)',
            'How It Works (4-Step Process)',
            'Rewards Section',
            'Call-to-Action (CTA) Configuration',
            'Gallery Image Integration',
            'Dynamic Content Loading',
            'Mobile Responsiveness'
          ]
        },
        {
          title: 'White-Label Deployment',
          description: 'How to deploy gamimap as a white-label solution for clients',
          icon: FiPackage,
          type: 'technical',
          url: `${baseUrl}/docs/WHITE_LABEL_DEPLOYMENT_ANALYSIS.md`,
          topics: [
            'Configuration Architecture',
            'Multi-Tenant Setup',
            'Domain & DNS Configuration',
            'Branding Customization',
            'Client-Specific Settings',
            'Database Isolation'
          ]
        }
      ]
    },
    app: {
      title: 'Mobile Apps (iOS & Android)',
      icon: FiSmartphone,
      color: 'from-primary-500 to-secondary-500',
      description: 'iOS and Android app development and deployment',
      roles: ['master_admin', 'admin', 'editor'],
      documents: [
        {
          title: 'Mobile App Features',
          description: 'Overview of the iOS and Android mobile applications',
          icon: FiSmartphone,
          type: 'guide',
          url: `${baseUrl}/docs/MOBILE_APP_FEATURES.md`,
          topics: [
            'User Authentication (Login/Register)',
            'Biometric Auth (Face ID/Touch ID on iOS)',
            'GPS Location Tracking',
            'Challenge Discovery with Distance Calculations',
            'Nearby Challenges Map View',
            'Achievement Progress Tracking',
            'NFT Collection Display',
            'Platform-Specific UI (Material Design / SwiftUI)',
            'Offline Capability'
          ]
        },
        {
          title: 'App Development Guide',
          description: 'Technical guide for building iOS and Android apps',
          icon: FiCode,
          type: 'technical',
          url: `${baseUrl}/docs/APP_DEVELOPMENT_GUIDE.md`,
          topics: [
            'Android: Gradle 8.5, Kotlin 1.9, SDK 34',
            'iOS: Swift 6.2.3, SwiftUI, xtool (Linux)',
            'White-Label Configuration System',
            'API Integration & Networking',
            'GPS Services Configuration',
            'Build Commands & Product Flavors',
            'Device Deployment & Testing',
            'Version Management'
          ]
        },
        {
          title: 'App Implementation Guide',
          description: 'Complete API reference & feature specs for mobile app development - endpoints, request/response formats, UI requirements',
          icon: FiCode,
          type: 'technical',
          url: `/documentation/app-implementation-guide.html`,
          topics: [
            'Events System with iCal Calendar (NEW)',
            'Route Challenges & Waypoints (NEW)',
            'Teasers / Promotional Carousel (NEW)',
            'Time Restrictions & Countdown UI (NEW)',
            'QR Code & Passphrase Verification (NEW)',
            'Challenge Ratings & Reviews (NEW)',
            'NFT Gallery, Minting & Claim Flow',
            'Onboarding & User Preferences',
            'All Public API Endpoints with JSON Examples',
            'Database Enum Reference'
          ]
        },
        {
          title: 'App Downloads',
          description: 'Latest app versions and installation instructions',
          icon: FiDownload,
          type: 'download',
          url: `${baseUrl}/docs/APP_DOWNLOADS.md`,
          topics: [
            'Android: v1.3-debug (~15 MB)',
            'iOS: Development builds via xtool',
            'Installation Instructions',
            'Permissions Required',
            'Troubleshooting Common Issues'
          ]
        }
      ]
    },
    blockchain: {
      title: 'Blockchain & NFT System',
      icon: FiLayers,
      color: 'from-orange-500 to-red-500',
      description: 'NFT minting guides and wallet management',
      roles: ['master_admin', 'admin'],
      documents: [
        {
          title: 'NFT Minting System',
          description: 'How NFT minting works and how to configure it',
          icon: FiAward,
          type: 'technical',
          url: `${baseUrl}/docs/NFT_MINTING_GUIDE.md`,
          topics: [
            'NFT Template Designer',
            'Rarity System (Common to Legendary)',
            'Challenge-Based NFT Rewards',
            'On-Demand Minting Process',
            'Custodial Wallet Model',
            'NFT Metadata & IPFS Storage',
            'Blockchain Explorer Integration',
            'Wallet Claim Process (planned)'
          ]
        },
        {
          title: 'Custodial Wallet System',
          description: 'Understanding the custodial wallet architecture for users',
          icon: FiShield,
          type: 'technical',
          url: `${baseUrl}/docs/CUSTODIAL_WALLET_SYSTEM.md`,
          topics: [
            'Phase 1: Earn (Achievement Completion)',
            'Phase 2: Mint (NFT Creation)',
            'Phase 3: Claim (External Wallet Transfer - planned)',
            'Security Best Practices',
            'User Wallet Generation',
            'Transaction Cost Management'
          ]
        },
        {
          title: 'Blockchain Setup Scripts',
          description: 'Scripts for wallet creation, opt-in, and testing',
          icon: FiCode,
          type: 'technical',
          url: `${baseUrl}/docs/BLOCKCHAIN_SETUP_SCRIPTS.md`,
          topics: [
            'Create Algorand Wallet',
            'Generate Custodial Wallets',
            'Opt-In to NFT Collections',
            'Test NFT Minting',
            'Check Wallet Balances',
            'Encrypt/Decrypt Mnemonics',
            'Arweave Integration (experimental)'
          ]
        }
      ]
    },
    marketing: {
      title: 'Marketing & Business',
      icon: FiTrendingUp,
      color: 'from-secondary-500 to-secondary-500',
      description: 'Business strategy, market analysis, and growth plans',
      roles: ['master_admin', 'admin', 'editor'],
      documents: [
        {
          title: 'Product Overview',
          description: 'Comprehensive overview of gamimap platform and use cases',
          icon: FiFileText,
          type: 'guide',
          url: `${baseUrl}/docs/PRODUCT_OVERVIEW.md`,
          topics: [
            'What is gamimap?',
            'Core Product Concept',
            'Tourism Board Partnerships',
            'Urban Discovery & Traffic Distribution',
            'Corporate & Educational Tourism',
            'Event & Festival Enhancement',
            'Market Opportunity',
            'Technology Stack'
          ]
        },
        {
          title: 'Grant Applications',
          description: 'Blockchain grant strategies and applications',
          icon: FiDollarSign,
          type: 'business',
          url: `${baseUrl}/docs/CAMINO_APPLICATION.md`,
          topics: [
            'Algorand Accelerator 2025',
            'Camino Network Partnership',
            'Web3 Grant Opportunities',
            'Competitive Analysis',
            'Funding Strategy'
          ]
        },
        {
          title: 'Competitive Analysis',
          description: 'Market positioning and competitive landscape',
          icon: FiTarget,
          type: 'business',
          url: `${baseUrl}/docs/COMPETITIVE_ANALYSIS_2025.md`,
          topics: [
            'Tourism Gamification Market',
            'Blockchain Tourism Projects',
            'Location-Based Services',
            'NFT Reward Systems',
            'White-Label Platforms',
            'gamimap Advantages'
          ]
        },
        {
          title: 'Market & Opportunity Report (Co-Founder)',
          description: 'Fact-checked market, competitor, userbase, business-model and risk analysis for sales & business development. Opens the branded PDF; editable Word (.docx) and LibreOffice (.odt) versions are in the /docs folder.',
          icon: FiTrendingUp,
          type: 'business',
          url: `${baseUrl}/docs/gamimap_market_research_cofounder.pdf`,
          topics: [
            'DMO software landscape & the consumer-engagement gap (Simpleview, Outdooractive)',
            'Competitor comparison — 8 vendors (Goosechase, Loquiz, Actionbound, CrowdRiff, Regiondo, Placer.ai, TOURDATA)',
            'Userbase: Strava, Komoot, AllTrails, Geocaching — and how gamimap wins them',
            'Business model & go-to-market (DMO SaaS, sponsorship, EU digitalisation funding)',
            'Market size & trends (smart tourism, gamification, overtourism)',
            'Digital rewards positioned as portability & permanence (not "NFTs")',
            'Honest risks, open questions & a concise opportunity thesis'
          ]
        },
        {
          title: 'Dynamic Equity Model',
          description: 'Founder equity distribution using Slicing Pie methodology',
          icon: FiUsers,
          type: 'business',
          url: `${baseUrl}/docs/EQUITY_MODEL.md`,
          topics: [
            'Slicing Pie Model',
            'Slice Calculation (Time, Cash, Equipment)',
            'Vesting Schedule & Cliff Period',
            'Role-Specific Milestone Bonuses',
            'Tech & Design Milestones',
            'Sales & Partnership Milestones',
            'Freeze Triggers & Exit Rules',
            'Fair Hours Regelung'
          ]
        },
        {
          title: 'Funding Opportunities 2025-2026',
          description: 'Comprehensive guide to grants, hackathons & accelerators for blockchain gaming/NFT projects',
          icon: FiDollarSign,
          type: 'business',
          url: `/documentation/funding-opportunities.html`,
          topics: [
            'Hackathons ($10k+ prizes) - Casper, Polygon, ETHGlobal, Solana',
            'Gaming/NFT Grants - Arbitrum ($190M), Immutable, Sui, Avalanche',
            'Berlin & Brandenburg Funding - IBB, ILB, Startup Stipendium',
            'German Federal Programs - FFG, DeepTech Fund, HTGF',
            'EU Funding - Horizon Europe, European Blockchain Sandbox',
            'Chain Comparison for Mass NFT Minting',
            'Recommended Action Plan & Deadlines'
          ]
        },
        {
          title: 'Future Features Roadmap',
          description: 'Comprehensive planning document for upcoming features including rewards, events, analytics, and blockchain integration',
          icon: FiTrendingUp,
          type: 'business',
          url: `/documentation/future-features-roadmap.html`,
          topics: [
            'Events System with iCal Calendar Integration (Implemented)',
            'Multi-Chain Blockchain (Camino Network)',
            'Achievement Celebrations (Progressive Reveal, Story Cards)',
            'Reward & Partner System with Fraud Prevention',
            'Safety & Hazard Warning Integration',
            'Visitor Analytics & Hotspot Detection',
            'User Preferences & Onboarding Carousel',
            'QR Code & Passphrase Verification (Implemented)',
            'Timed Challenges with Countdown UI',
            'Social Features Integration Points',
            'Implementation Priority Matrix (Q1-Q4 2026)'
          ]
        },
        {
          title: 'SaaS Cost Analysis',
          description: 'Comprehensive cost analysis for all SaaS APIs and infrastructure - scaling from 100 to 20,000 users',
          icon: FiDollarSign,
          type: 'business',
          url: `${baseUrl}/docs/SAAS_COST_ANALYSIS.md`,
          topics: [
            'Anthropic Claude API Costs (Challenge Generation)',
            'Replicate AI Models (Cartoonization $0.001-$0.05)',
            'Blockchain Costs - Camino €0.00024/NFT',
            'Email Service (Mailgun Tiers)',
            'IPFS Storage (Filebase)',
            'Infrastructure Scaling (Hetzner €4-€213/mo)',
            'Complete Cost Tables (100-20K Users)',
            'Budget vs Premium Model Comparison',
            'Google Maps Pricing Analysis',
            'Pricing Strategy Recommendations'
          ]
        },
        {
          title: 'Pricing Model',
          description: 'White-label platform pricing including setup fees, SaaS tiers, content packages, and market entry strategy',
          icon: FiDollarSign,
          type: 'business',
          url: `${baseUrl}/docs/pricing-model.html`,
          topics: [
            'CMS Capabilities (No-Code Configuration)',
            'Setup + Design Pricing (Options A-D)',
            'Monthly SaaS Tiers (Starter, Professional, Enterprise)',
            'NFT Pool Model & Cost Analysis',
            'Content Creation Packages',
            'Bundle Packages with Discounts',
            'Market Entry Strategy (Case Study Partners)',
            'Limited Scope Trials & Revenue Share',
            'Competitor Analysis',
            'Custom App Development Comparison'
          ]
        }
      ]
    },
    technical: {
      title: 'Technical Documentation',
      icon: FiCode,
      color: 'from-purple-500 to-indigo-500',
      description: 'Complete technical reference for developers',
      roles: ['master_admin'],
      documents: [
        {
          title: 'Technology Stack Overview',
          description: 'Branded 4-page PDF overview of the full gamimap tech stack — architecture, backend, frontend, mobile apps, blockchain/NFT, AI services, and infrastructure. Designed for developer onboarding and technology partner briefings.',
          icon: FiServer,
          type: 'technical',
          url: `${baseUrl}/docs/gamimap_tech_stack_overview.pdf`,
          topics: [
            'System Architecture Diagram (Nginx → Express → PostgreSQL/PostGIS)',
            'Backend: Node.js 18, Express 4, Socket.IO, JWT, Sharp',
            'Database: PostgreSQL 16 + PostGIS 3.4 (spatial queries)',
            'CMS Frontend: React 18, Tailwind CSS, Leaflet, Recharts, xterm.js',
            'Mobile: Android (Kotlin 1.9, SDK 34) + iOS (Swift 6, SwiftUI)',
            'AI: Claude (challenge generation) + Replicate (NFT cartoonification)',
            'Blockchain: Algorand, Camino (EVM), Polygon — multi-chain NFT minting',
            'Infrastructure: Hetzner Cloud, Nginx, PM2, Let\'s Encrypt, white-label multi-tenant'
          ]
        },
        {
          title: 'Technical Documentation Hub',
          description: 'Comprehensive multi-page technical documentation covering architecture, API, frontend, database, blockchain, security, and deployment',
          icon: FiCode,
          type: 'technical',
          url: `/documentation/index.html`,
          topics: [
            'System Architecture & Design Patterns',
            'Backend API (100+ endpoints)',
            'Frontend Components & Pages',
            'Database Schema (20+ tables)',
            'Blockchain & NFT Integration',
            'Security Architecture',
            'External Services (AI, Storage)',
            'Deployment & Operations'
          ]
        }
      ]
    },
    masterOnly: {
      title: '🔒 Master-Only Documentation',
      icon: FiLock,
      color: 'from-primary-600 to-primary-600',
      description: 'Platform owner documentation (sensitive credentials, wallet management)',
      roles: ['master_admin'],
      documents: [
        {
          title: '🔒 Custodial Wallet System',
          description: 'Master wallet and custodial architecture documentation',
          icon: FiShield,
          type: 'master',
          url: `${baseUrl}/docs/CUSTODIAL_WALLET_SYSTEM.md`,
          topics: [
            'Master Wallet Security',
            'Mnemonic Encryption (AES-256-CBC)',
            'User Wallet Generation',
            'NFT Distribution Model',
            'Transaction Cost Management',
            'Blockchain Network Configuration',
            'Opt-In Requirements (Algorand)',
            'Security Best Practices'
          ]
        },
        {
          title: '🔒 Terminal Access Guide',
          description: 'Secure browser-based terminal with 2FA authentication',
          icon: FiCpu,
          type: 'master',
          url: `${baseUrl}/docs/TERMINAL_ACCESS_GUIDE.md`,
          topics: [
            'TOTP 2FA Setup (Google Authenticator)',
            'WebSocket Terminal Session',
            'Security Features & Audit Logging',
            'Session Management',
            'Command History & Timeout',
            'Emergency Access Procedures'
          ]
        },
        {
          title: '🔒 Progress Summary',
          description: 'Development milestones and implementation status',
          icon: FiBarChart2,
          type: 'master',
          url: `${baseUrl}/docs/PROGRESS_SUMMARY.md`,
          topics: [
            'Completed Features',
            'Current Development Status',
            'Pending Implementations',
            'Known Issues & Bugs',
            'Performance Metrics',
            'Future Roadmap',
            'Technical Debt'
          ]
        }
      ]
    }
  };

  const stats = [
    { label: 'Documentation Sections', value: '7', icon: FiBook },
    { label: 'Feature Categories', value: '25+', icon: FiBarChart2 },
    { label: 'Implementation Guides', value: '20+', icon: FiFileText },
    { label: 'Technical Topics', value: '100+', icon: FiCode },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-600">
            <FiBook className="inline-block mr-2" />
            {t('cms.documentation.title')}
          </h1>
          <p className="text-gray-600">{t('cms.documentation.subtitle')}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Icon className="h-8 w-8 text-primary-600" />
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-secondary-500 font-heading">{stat.value}</p>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Documentation Sections */}
      <div className="space-y-6">
        {Object.keys(documentSections)
          .filter((sectionKey) => {
            const section = documentSections[sectionKey];
            return section.roles && section.roles.includes(userRole);
          })
          .map((sectionKey) => {
            const section = documentSections[sectionKey];
            const SectionIcon = section.icon;
            const isExpanded = expandedSections[sectionKey];

          return (
            <div key={sectionKey} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Section Header */}
              <button
                onClick={() => toggleSection(sectionKey)}
                className={`w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors`}
              >
                <div className="flex items-center">
                  <div className={`p-3 rounded-lg bg-gradient-to-r ${section.color} mr-4`}>
                    <SectionIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-left">
                    <h2 className="text-2xl font-bold text-secondary-500 font-heading">{section.title}</h2>
                    <p className="text-gray-600 text-sm mt-1">{section.description}</p>
                  </div>
                </div>
                <div className="text-gray-400">
                  {isExpanded ? <FiChevronUp className="h-6 w-6" /> : <FiChevronDown className="h-6 w-6" />}
                </div>
              </button>

              {/* Section Content */}
              {isExpanded && (
                <div className="border-t border-gray-200 p-6 bg-gray-50">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {section.documents.map((doc, docIndex) => {
                      const DocIcon = doc.icon;
                      const badgeColor = doc.type === 'master' ? 'bg-primary-600 text-white' :
                                        doc.type === 'guide' ? 'bg-blue-100 text-blue-800' :
                                        doc.type === 'technical' ? 'bg-green-100 text-green-800' :
                                        doc.type === 'business' ? 'bg-primary-100 text-primary-800' :
                                        'bg-orange-100 text-orange-800';

                      return (
                        <div key={docIndex} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-all">
                          <div className="flex items-start mb-4">
                            <DocIcon className="h-6 w-6 text-primary-600 mr-3 mt-1 flex-shrink-0" />
                            <div className="flex-1">
                              <div className="flex items-start justify-between mb-2">
                                <h3 className="text-lg font-bold text-gray-900">{doc.title}</h3>
                                <span className={`px-2 py-1 ${badgeColor} text-xs font-semibold rounded-full ml-2 whitespace-nowrap`}>
                                  {doc.type}
                                </span>
                              </div>
                              <p className="text-gray-600 text-sm mb-4">{doc.description}</p>

                              {/* Topics List */}
                              <div className="mb-4">
                                <h4 className="text-sm font-semibold text-gray-700 mb-2">Topics Covered:</h4>
                                <ul className="space-y-1">
                                  {doc.topics.slice(0, 5).map((topic, topicIndex) => (
                                    <li key={topicIndex} className="text-sm text-gray-600 flex items-start">
                                      <span className="text-primary-600 mr-2">•</span>
                                      <span>{topic}</span>
                                    </li>
                                  ))}
                                  {doc.topics.length > 5 && (
                                    <li className="text-sm text-gray-500 italic ml-4">
                                      + {doc.topics.length - 5} more topics...
                                    </li>
                                  )}
                                </ul>
                              </div>

                              {/* Action Button - All docs open in new tab */}
                              {doc.url ? (
                                <a
                                  href={doc.url.startsWith('/documentation/') ? doc.url : doc.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-primary inline-flex items-center transition-colors text-sm"
                                >
                                  <FiExternalLink className="w-4 h-4 mr-2" />
                                  {t('cms.documentation.openInNewTab')}
                                </a>
                              ) : (
                                <div className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-600 font-medium rounded-lg text-sm">
                                  <FiFileText className="w-4 h-4 mr-2" />
                                  In CMS Dashboard
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Support Footer */}
      <div className="mt-8 text-center text-gray-500">
        <p className="mb-2">
          Need help? Expand the sections above to find detailed documentation for each component.
        </p>
        <p className="text-sm">
          For technical support, refer to the{' '}
          <span className="text-primary-600 font-medium">CMS & Admin Dashboard</span> section or{' '}
          <span className="text-primary-600 font-medium">Deployment</span> guides.
        </p>
      </div>

      {/* Document Viewer Modal */}
      {viewerOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={closeViewer}
          />

          {/* Modal */}
          <div className="absolute inset-4 md:inset-8 lg:inset-12 bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center">
                <FiFileText className="w-5 h-5 text-primary-600 mr-3" />
                <h2 className="text-lg font-semibold text-gray-900">{viewerTitle}</h2>
              </div>
              <button
                onClick={closeViewer}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto">
              {viewerLoading && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading document...</p>
                  </div>
                </div>
              )}

              {viewerError && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center p-8 max-w-md">
                    <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                      <FiAlertTriangle className="w-8 h-8 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
                    <p className="text-gray-600 mb-4">{viewerError}</p>
                    <button
                      onClick={closeViewer}
                      className="btn btn-secondary"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}

              {!viewerLoading && !viewerError && viewerContent && (
                <iframe
                  srcDoc={viewerContent}
                  title={viewerTitle}
                  className="w-full h-full border-0"
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documentation;